import React from "react";
import Chart from "react-apexcharts";
import { BsTrophy } from "react-icons/bs";
import persianNumberFormat from "../../helper/PersianNumberFormat";
import { salesFor } from "./support_sales_metrics";
import { CHART_COLORS, CHART_PALETTE } from "./charts/palette";

export const formatNumber = (value) => persianNumberFormat(value, true);
const percent = (value, total) => total > 0 ? (value / total) * 100 : 0;
const formatPercent = (value) => `${persianNumberFormat(Number(value.toFixed(1)))}٪`;

export function Amount({ value, className = "" }) {
  return (
    <span className={`inline-flex flex-wrap items-baseline gap-1.5 ${className}`}>
      <span className="tabular-nums">{formatNumber(value)}</span>
      <span className="text-xs font-normal text-app-text-muted">ریال</span>
    </span>
  );
}

export function CountBadge({ count }) {
  return (
    <span
      className="inline-flex shrink-0 items-center rounded-md bg-app-muted px-2 py-1 text-xs font-medium text-app-text-muted"
      aria-label={`${persianNumberFormat(count)} مورد`}
    >
      {formatNumber(count)} مورد
    </span>
  );
}

export function SummaryCard({ title, metric, icon, teal = false, children }) {
  return (
    <article aria-label={title} className="min-w-0 overflow-hidden rounded-xl border border-app-border bg-app-surface shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className={teal ? "text-teal-600" : "text-blue-600"} aria-hidden="true">
            {icon}
          </span>
          <h3 className="text-xs font-semibold text-app-text">{title}</h3>
          <CountBadge count={metric.count} />
        </div>
        <Amount value={metric.amount} className="text-sm font-bold text-app-text" />
      </div>
      {children && <div className="border-t border-app-border">{children}</div>}
    </article>
  );
}

export function DonutCard({ title, periodLabel, segments, measure, countUnit = "مورد", emptyText = "فروشی در این بازه ثبت نشده است" }) {
  segments = segments.map((segment, index) => ({ ...segment, color: segment.color || CHART_PALETTE[index % CHART_PALETTE.length] }));
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  const unit = measure === "amount" ? "ریال" : countUnit;
  const reducedMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  const options = {
    chart: {
      type: "donut",
      background: "transparent",
      fontFamily: "inherit",
      foreColor: "rgb(var(--color-app-text-muted))",
      parentHeightOffset: 0,
      toolbar: { show: false },
      animations: { enabled: !reducedMotion },
    },
    labels: segments.map((segment) => segment.label),
    colors: segments.map((segment) => segment.color),
    legend: { show: false },
    stroke: { width: 3, colors: ["rgb(var(--color-app-surface))"] },
    grid: { padding: { top: 0, right: 0, bottom: 0, left: 0 } },
    dataLabels: {
      enabled: true,
      formatter: (value) => formatPercent(Number(value)),
      style: { fontSize: "11px", fontWeight: 600, colors: ["#ffffff"] },
      dropShadow: { enabled: false },
    },
    plotOptions: {
      pie: {
        expandOnClick: true,
        dataLabels: { minAngleToShowLabel: 15 },
        donut: {
          size: "74%",
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: "11px",
              color: "rgb(var(--color-app-text-muted))",
              formatter: (label) => `${label} · ${unit}`,
            },
            value: {
              show: true,
              fontSize: measure === "amount" ? "12px" : "24px",
              fontWeight: 700,
              color: "rgb(var(--color-app-text))",
              formatter: (value) => formatNumber(Number(value)),
            },
            total: {
              show: true,
              showAlways: false,
              label: "مجموع",
              fontSize: "11px",
              color: "rgb(var(--color-app-text-muted))",
              formatter: () => formatNumber(total),
            },
          },
        },
      },
    },
    tooltip: {
      theme: "dark",
      y: {
        formatter: (value) => `${formatNumber(value)} ${unit} (${formatPercent(percent(value, total))})`,
      },
    },
  };

  return (
    <article aria-label={title} className="flex min-w-0 flex-col rounded-xl border border-app-border bg-app-surface shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-app-border px-4 py-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-app-text">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: segments[0].color }} aria-hidden="true" />
          {title}
        </h3>
        <span className="text-xs text-app-text-subtle">{periodLabel}</span>
      </div>
      <div role="img" aria-label={`${title} بر اساس ${measure === "amount" ? "مبلغ" : "تعداد"}`}
        className="support-sales-donut mx-auto my-2 h-56 w-full max-w-64 shrink-0">
        {total > 0 ? (
          <Chart type="donut" options={options} series={segments.map((segment) => segment.value)} height={224} width="100%" />
        ) : (
          <div className="mx-auto flex h-56 w-56 items-center justify-center p-7" aria-hidden="true">
            <div className="flex h-full w-full flex-col items-center justify-center rounded-full border-[18px] border-app-border text-center">
              <span className="text-2xl font-bold text-app-text">{formatNumber(0)}</span>
              <span className="mt-1 text-xs text-app-text-muted">{unit}</span>
            </div>
          </div>
        )}
      </div>
      {total === 0 && <p className="-mt-2 mb-3 text-center text-xs text-app-text-muted">{emptyText}</p>}
      <ul className="mt-auto space-y-2 border-t border-app-border px-4 py-3 text-xs">
        {segments.map((segment) => (
          <li key={segment.label} className="flex flex-wrap items-center justify-between gap-2">
            <span className="flex items-center gap-2 text-app-text-muted">
              <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: segment.color }} aria-hidden="true" />
              {segment.label}
            </span>
            <span className="font-medium tabular-nums text-app-text">
              {formatNumber(segment.value)} {unit}
              <span className="mr-2 font-normal text-app-text-subtle">{formatPercent(percent(segment.value, total))}</span>
            </span>
          </li>
        ))}
      </ul>
    </article>
  );
}

export function Leaderboard({ items }) {
  return (
    <article aria-label="جدول برترین پشتیبان‌ها" className="flex min-w-0 flex-col rounded-xl border border-app-border bg-app-surface shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-app-border px-4 py-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-app-text">
          <BsTrophy className="text-amber-500" aria-hidden="true" /> برترین پشتیبان‌ها
        </h3>
        <span className="text-xs text-app-text-subtle">این ماه</span>
      </div>
      <p className="px-4 pt-3 text-xs leading-5 text-app-text-muted">رتبه‌بندی بر اساس ارزش فروش خدمات و مشاوره‌های پرداخت‌شده</p>
      {items.length === 0 ? (
        <div className="flex min-h-64 flex-1 flex-col items-center justify-center gap-3 px-6 py-8 text-center">
          <BsTrophy className="text-3xl text-app-text-subtle" aria-hidden="true" />
          <p className="text-sm text-app-text-muted">هنوز فروش منتسبی در این ماه ثبت نشده است.</p>
        </div>
      ) : (
        <ol aria-label="رتبه‌بندی فروش ماه جاری" className="max-h-80 space-y-2 overflow-y-auto p-3">
          {items.map((item) => (
            <li key={item.support_id} className="rounded-lg border border-app-border bg-app-muted p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`flex h-8 min-w-8 items-center justify-center rounded-lg px-1 text-sm font-semibold ${item.rank === 1 ? "bg-amber-500/15 text-amber-700" : "bg-app-surface text-app-text-muted"}`}>
                  {persianNumberFormat(item.rank)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="break-words text-sm font-semibold text-app-text">{item.support_name || "پشتیبان"}</p>
                  <p className="mt-0.5 text-xs text-app-text-subtle">شناسه {persianNumberFormat(item.support_id)}</p>
                </div>
                <Amount value={item.sales.total.amount} className="text-sm font-semibold text-teal-600" />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 rounded-md bg-app-surface p-2 text-center text-xs">
                <span className="text-app-text-muted"><strong className="ml-1 text-blue-500">{formatNumber(item.sales.services.count)}</strong>خدمت</span>
                <span className="text-app-text-muted"><strong className="ml-1 text-teal-600">{formatNumber(item.sales.reservations.count)}</strong>مشاوره پرداخت‌شده</span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </article>
  );
}

export function SupportCard({ item, period, assignedTotal }) {
  const stats = salesFor(item, period);
  const otherPeriod = period === "month" ? "today" : "month";
  const other = salesFor(item, otherPeriod);
  const share = percent(stats.total.amount, assignedTotal);
  const name = item.support_name || "پشتیبان";

  return (
    <article aria-label={`عملکرد ${name} - ${item.support_id}`} className="min-w-0 rounded-xl border border-app-border bg-app-surface p-4 shadow-sm">
      <div className="mb-4 flex items-center gap-2.5">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-amber-500/25 bg-amber-500/5 text-sm font-semibold text-app-text" aria-hidden="true">
          {Array.from(name.trim())[0]}
        </span>
        <div className="min-w-0 flex-1">
          <h4 className="break-words text-sm font-semibold text-app-text">{name}</h4>
          <p className="mt-0.5 text-xs text-app-text-subtle">شناسه {persianNumberFormat(item.support_id)}</p>
        </div>
        <CountBadge count={stats.total.count} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[
          { label: "خدمات", metric: stats.services, color: "text-blue-500" },
          { label: "مشاوره‌های پرداخت‌شده", metric: stats.reservations, color: "text-teal-600" },
        ].map(({ label, metric, color }) => (
          <div key={label} className="min-w-0 rounded-lg bg-app-muted p-2.5">
            <p className="mb-2 text-xs text-app-text-muted">{label}</p>
            <Amount value={metric.amount} className={`break-all text-sm font-semibold ${color}`} />
            <p className="mt-1 text-xs text-app-text-subtle">{formatNumber(metric.count)} مورد</p>
          </div>
        ))}
      </div>
      <div className="mt-4">
        <div className="mb-1.5 flex items-center justify-between gap-2 text-xs text-app-text-muted">
          <span>سهم از فروش منتسب {period === "month" ? "این ماه" : "امروز"}</span>
          <span className="font-semibold text-app-text">{formatPercent(share)}</span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-app-muted" aria-hidden="true">
          <div className="h-full rounded-full" style={{ width: `${Math.min(share, 100)}%`, backgroundColor: CHART_COLORS.pink }} />
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-app-border pt-3 text-xs">
        <span className="text-app-text-muted">فروش {otherPeriod === "today" ? "امروز" : "این ماه"} · {formatNumber(other.total.count)} مورد</span>
        <Amount value={other.total.amount} className="font-medium text-app-text" />
      </div>
    </article>
  );
}
